import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { visuallyHidden } from '@mui/utils';
import InfoIcon from './Widgets/InfoIcon';
import useDragSortTable, { getNestedValue } from '../Utility/hooks/useDragSortTable';
import './BiztantuDragTable.css';

// Memoized Table Row to prevent unnecessary re-renders of all rows during drag
const DraggableRow = React.memo(
  function DraggableRow({
    row,
    index,
    rowKey,
    columns,
    isReorderMode,
    isAnyRowEditing,
    isCurrentRowEditing,
    isDragged,
    isDragOver,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,
    onDragEnd,
    isReorderable,
    dragTooltip
  }) {
    const isDraggable = isReorderMode && !isAnyRowEditing && !row.isNew;

    const rowClasses = [
      'biztantu-drag-row',
      index % 2 === 0 ? 'biztantu-drag-row-even' : 'biztantu-drag-row-odd',
      isDragged ? 'is-dragged' : '',
      isDragOver ? 'is-drag-over' : '',
      isCurrentRowEditing ? 'is-editing' : ''
    ].filter(Boolean).join(' ');

    const handleClasses = [
      'biztantu-drag-handle',
      isDraggable ? 'is-active' : ''
    ].filter(Boolean).join(' ');

    return (
      <TableRow
        className={rowClasses}
        draggable={isDraggable}
        onDragStart={(e) => onDragStart(e, index)}
        onDragOver={(e) => onDragOver(e, index)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, index)}
        onDragEnd={onDragEnd}
      >
        {/* 1. Drag Handle Cell */}
        {isReorderable && (
          <TableCell align="center" sx={{ width: 55 }}>
            <Tooltip title={isDraggable ? (dragTooltip || '') : ''}>
              <Box className={handleClasses}>
                <DragIndicatorIcon fontSize="small" />
              </Box>
            </Tooltip>
          </TableCell>
        )}

        {/* 2. Dynamic Column Cells */}
        {columns.map((col) => {
          const rawValue = getNestedValue(row, col.field);
          const cellContent = col.renderCell
            ? col.renderCell({
                row,
                index,
                value: rawValue,
                isEditing: isCurrentRowEditing
              })
            : (
              <Typography variant="body2" sx={{ color: '#000000' }}>
                {rawValue ?? '-'}
              </Typography>
            );

          const effectiveMinWidth = isCurrentRowEditing && col.editMinWidth ? col.editMinWidth : col.minWidth;

          return (
            <TableCell
              key={col.field}
              align={col.align || 'left'}
              sx={{
                width: col.width,
                minWidth: effectiveMinWidth,
                ...(col.cellSx || {})
              }}
            >
              {cellContent}
            </TableCell>
          );
        })}
      </TableRow>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.row === nextProps.row &&
      prevProps.index === nextProps.index &&
      prevProps.isReorderMode === nextProps.isReorderMode &&
      prevProps.isAnyRowEditing === nextProps.isAnyRowEditing &&
      prevProps.isCurrentRowEditing === nextProps.isCurrentRowEditing &&
      prevProps.isDragged === nextProps.isDragged &&
      prevProps.isDragOver === nextProps.isDragOver &&
      prevProps.columns === nextProps.columns
    );
  }
);


/**
 * BiztantuDragTable
 * Reusable MUI Table with HTML5 drag-and-drop reordering, column click sorting,
 * action bar slots, and grid styling matching Biztantu DataGrid.
 */
const BiztantuDragTable = ({
  columns = [],
  rows = [],
  rowKey = 'id',
  sequenceField = 'sequence',
  isReorderable = true,
  onConfirmReorder,
  onAddClick,
  addBtnLabel = 'Add',
  isAddDisabled = false,
  actionBarLeft,
  actionBarRight,
  loading = false,
  emptyMessage = 'No records found',
  maxHeight = 'calc(100vh - 350px)',
  isAnyRowEditing = false,
  editingRowId = null,
  reorderBtnLabel = 'Edit Order',
  confirmBtnLabel = 'Done',
  cancelBtnLabel = 'Cancel',
  reorderInfoKey = 'CONF_LU_REORDER_INFO',
  dragTooltip = '',
  onReorderModeChange
}) => {
  const {
    displayRows,
    isReorderMode,
    isSavingOrder,
    draggedIndex,
    dragOverIndex,
    orderBy,
    order,
    handleRequestSort,
    handleEnterReorderMode,
    handleCancelReorder,
    handleConfirmReorder,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd
  } = useDragSortTable({
    rows,
    rowKey,
    sequenceField,
    onConfirmReorder,
    isAnyRowEditing,
    onReorderModeChange
  });

  return (
    <Box className="biztantu-drag-table-root">
      {/* 1. Action Bar */}
      <Box className="biztantu-drag-table-action-bar">
        {/* Left side slot (e.g. client dropdown or search) */}
        <Box sx={{ flex: 1, minWidth: '250px', display: 'flex', alignItems: 'flex-end' }}>
          {actionBarLeft}
        </Box>

        {/* Right side controls */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pb: '4px' }}>
          {actionBarRight}

          {isReorderable && (
            isReorderMode ? (
              <>
                <Button
                  color="primary"
                  className="biztantu-reorder-btn"
                  disabled={isSavingOrder}
                  onClick={handleCancelReorder}
                  sx={{
                    border: '1px solid black !important',
                    color: 'black !important',
                    borderRadius: '13px !important',
                    padding: '3px 12px !important',
                    minWidth: 'unset !important'
                  }}
                >
                  {cancelBtnLabel}
                </Button>
                <Button
                  color="primary"
                  className="biztantu-reorder-btn"
                  disabled={isSavingOrder}
                  onClick={handleConfirmReorder}
                  sx={{
                    border: '1px solid black !important',
                    color: 'black !important',
                    borderRadius: '13px !important',
                    padding: '3px 12px !important',
                    margin: '0 10px !important',
                    minWidth: 'unset !important'
                  }}
                >
                  {isSavingOrder ? 'Saving...' : confirmBtnLabel}
                </Button>
              </>
            ) : (
              <Button
                className="biztantu-sec-btn"
                disabled={isAnyRowEditing || rows.length <= 1}
                onClick={handleEnterReorderMode}
              >
                {reorderBtnLabel}
              </Button>
            )
          )}

          {onAddClick && (
            <Button
              className="biztantu-sec-btn"
              disabled={isAnyRowEditing || isReorderMode || isAddDisabled}
              onClick={onAddClick}
            >
              {addBtnLabel}
            </Button>
          )}
        </Stack>
      </Box>

      {/* 2. Reorder Mode Guidance using InfoIcon */}
      {isReorderMode && (
        <InfoIcon text={reorderInfoKey} />
      )}

      {/* 3. Table Container */}
      <TableContainer
        component={Paper}
        elevation={0}
        className="biztantu-drag-table-container"
        sx={{ maxHeight }}
      >
        <Table
          stickyHeader
          size="small"
          aria-label="Biztantu Drag Table"
          className="biztantu-drag-table"
        >
          {/* Table Header */}
          <TableHead>
            <TableRow className="biztantu-drag-table-head-row">
              {/* Drag Handle Column Header */}
              {isReorderable && (
                <TableCell align="center" sx={{ width: 55, py: 1.5 }}>
                  <Tooltip title={dragTooltip || reorderBtnLabel || ''}>
                    <DragIndicatorIcon sx={{ color: isReorderMode ? '#1342C9' : '#BBB', verticalAlign: 'middle' }} />
                  </Tooltip>
                </TableCell>
              )}

              {/* Data Column Headers */}
              {columns.map((col) => {
                const isSortable = col.sortable !== false && !isReorderMode;
                const isSortActive = orderBy === col.field;

                return (
                  <TableCell
                    key={col.field}
                    align={col.align || 'left'}
                    sortDirection={isSortActive ? order : false}
                    sx={{
                      width: col.width,
                      minWidth: col.minWidth,
                      py: 1.5,
                      ...(col.headerSx || {})
                    }}
                  >
                    {isSortable ? (
                      <TableSortLabel
                        active={isSortActive}
                        direction={isSortActive ? order : 'asc'}
                        onClick={() => handleRequestSort(col.field)}
                        sx={{
                          fontWeight: 600,
                          '&.Mui-active': { color: '#1342C9' }
                        }}
                      >
                        {col.headerName}
                        {isSortActive ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                        {col.headerName}
                      </Typography>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + (isReorderable ? 1 : 0)} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : displayRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (isReorderable ? 1 : 0)} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" sx={{ color: '#888' }}>
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayRows.map((row, index) => {
                const rowId = row[rowKey];
                const isCurrentRowEditing = editingRowId === rowId;
                const isDragged = draggedIndex === index;
                const isDragOver = dragOverIndex === index;

                return (
                  <DraggableRow
                    key={rowId}
                    row={row}
                    index={index}
                    rowKey={rowKey}
                    columns={columns}
                    isReorderMode={isReorderMode}
                    isAnyRowEditing={isAnyRowEditing}
                    isCurrentRowEditing={isCurrentRowEditing}
                    isDragged={isDragged}
                    isDragOver={isDragOver}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    isReorderable={isReorderable}
                    dragTooltip={dragTooltip || reorderBtnLabel || ''}
                  />
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};


export default React.memo(BiztantuDragTable);
